import React from 'react';
import PublicLegalLayout from '../Components/PublicLegalLayout';

function GeneralConditions() {
    const lastUpdated = new Date().toLocaleDateString();

    return (
        <PublicLegalLayout
            title="Terms and Conditions"
            lastUpdated={lastUpdated}
            description="The general conditions for using CueClub Manager to run club operations, games, scoreboards, queues, and statistics."
        >
            <div className="card legal-document-card">
                <div className="card-body">
                    <div className="legal-document-copy">

                            <section className="mb-4">
                                <h2 className="h3 mb-3">1. Acceptance of Terms</h2>
                                <p>
                                    By accessing and using our Club Management System (the "Platform"), you agree to be bound by
                                    these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not use
                                    our Platform.
                                </p>
                                <p>
                                    These Terms apply to all users, including club owners, managers, staff members, and players
                                    using our gaming club management services for snooker, pool, US pool, and 8-ball facilities.
                                </p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">2. Service Description</h2>
                                <p>Our Platform provides comprehensive club management solutions including:</p>

                                <h4 className="h5 mb-2">2.1 Core Features</h4>
                                <ul className="mb-3">
                                    <li><strong>Digital Scoreboards:</strong> Real-time game scoring and match tracking</li>
                                    <li><strong>Player Management:</strong> Member profiles, skill tracking, and statistics</li>
                                    <li><strong>Tournament Organization:</strong> Event scheduling, bracket management, and results</li>
                                    <li><strong>Financial Management:</strong> Payment processing, invoicing, and financial reporting</li>
                                    <li><strong>Analytics and Reporting:</strong> Game statistics, usage reports, and performance insights</li>
                                    <li><strong>Equipment Management:</strong> Table scheduling and maintenance tracking</li>
                                </ul>

                                <h4 className="h5 mb-2">2.2 Supported Games</h4>
                                <ul className="mb-3">
                                    <li>Snooker (15 reds format and variations)</li>
                                    <li>English Pool (8-ball and 9-ball)</li>
                                    <li>American Pool (US 8-ball and 9-ball)</li>
                                    <li>Custom game formats as configured by club administrators</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">3. User Accounts and Registration</h2>

                                <h4 className="h5 mb-2">3.1 Account Creation</h4>
                                <ul className="mb-3">
                                    <li>You must provide accurate and complete information when creating an account</li>
                                    <li>You are responsible for maintaining the security of your account credentials</li>
                                    <li>Each user may maintain only one active account unless authorized by us</li>
                                    <li>Club administrators may create and manage multiple player accounts</li>
                                </ul>

                                <h4 className="h5 mb-2">3.2 Account Types</h4>
                                <ul className="mb-3">
                                    <li><strong>Club Owner:</strong> Full administrative access and billing responsibility</li>
                                    <li><strong>Club Manager:</strong> Operational management and reporting access</li>
                                    <li><strong>Staff Member:</strong> Limited access to daily operations and scoring</li>
                                    <li><strong>Player:</strong> Personal statistics access and game participation</li>
                                </ul>

                                <h4 className="h5 mb-2">3.3 Account Responsibilities</h4>
                                <ul className="mb-3">
                                    <li>Notify us immediately of any unauthorized account access</li>
                                    <li>Maintain current and accurate contact information</li>
                                    <li>Comply with all applicable laws and regulations</li>
                                    <li>Use the Platform only for legitimate club management purposes</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">4. Subscription and Payment Terms</h2>

                                <h4 className="h5 mb-2">4.1 Subscription Plans</h4>
                                <p>We offer various subscription tiers with different features and usage limits:</p>
                                <ul className="mb-3">
                                    <li><strong>Basic Plan:</strong> Essential features for small clubs</li>
                                    <li><strong>Professional Plan:</strong> Advanced features and increased capacity</li>
                                    <li><strong>Enterprise Plan:</strong> Full feature set with custom integrations</li>
                                    <li><strong>Custom Plans:</strong> Tailored solutions for large organizations</li>
                                </ul>

                                <h4 className="h5 mb-2">4.2 Billing and Payments</h4>
                                <ul className="mb-3">
                                    <li>Subscription fees are billed monthly or annually as selected</li>
                                    <li>All payments are due in advance and non-refundable unless stated otherwise</li>
                                    <li>We accept major credit cards and electronic payment methods</li>
                                    <li>Late payments may result in service suspension after a 7-day grace period</li>
                                    <li>Price changes will be communicated 30 days in advance</li>
                                </ul>

                                <h4 className="h5 mb-2">4.3 Usage Limits</h4>
                                <ul className="mb-3">
                                    <li>Each plan includes specific limits on players, games, and data storage</li>
                                    <li>Exceeding limits may incur additional charges or require plan upgrades</li>
                                    <li>We will notify you when approaching usage limits</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">5. Acceptable Use Policy</h2>

                                <h4 className="h5 mb-2">5.1 Permitted Uses</h4>
                                <ul className="mb-3">
                                    <li>Managing gaming club operations and member activities</li>
                                    <li>Recording and tracking game statistics and tournament results</li>
                                    <li>Processing payments and managing club finances</li>
                                    <li>Communicating with club members and organizing events</li>
                                </ul>

                                <h4 className="h5 mb-2">5.2 Prohibited Activities</h4>
                                <ul className="mb-3">
                                    <li>Using the Platform for illegal gambling or betting operations</li>
                                    <li>Attempting to hack, disrupt, or gain unauthorized access to our systems</li>
                                    <li>Sharing account credentials or allowing unauthorized access</li>
                                    <li>Uploading malicious content or attempting to distribute malware</li>
                                    <li>Violating any applicable laws or regulations</li>
                                    <li>Harassing other users or creating fake player profiles</li>
                                    <li>Reverse engineering or attempting to copy our software</li>
                                </ul>

                                <h4 className="h5 mb-2">5.3 Content Standards</h4>
                                <ul className="mb-3">
                                    <li>All user-generated content must be appropriate for a gaming club environment</li>
                                    <li>No offensive language, discriminatory content, or inappropriate imagery</li>
                                    <li>Player names and club information must be accurate and legitimate</li>
                                    <li>Tournament and game data must be recorded honestly and accurately</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">6. Data and Statistics</h2>

                                <h4 className="h5 mb-2">6.1 Data Accuracy</h4>
                                <ul className="mb-3">
                                    <li>Users are responsible for ensuring accurate game scoring and data entry</li>
                                    <li>We provide tools for data correction but cannot guarantee 100% accuracy</li>
                                    <li>Disputed results should be resolved at the club level before recording</li>
                                </ul>

                                <h4 className="h5 mb-2">6.2 Statistics and Rankings</h4>
                                <ul className="mb-3">
                                    <li>Player statistics are calculated based on recorded game data</li>
                                    <li>Rankings and leaderboards reflect performance within your club system</li>
                                    <li>We reserve the right to exclude suspicious or invalid game data</li>
                                    <li>Historical statistics may be preserved even after account cancellation</li>
                                </ul>

                                <h4 className="h5 mb-2">6.3 Data Export</h4>
                                <ul className="mb-3">
                                    <li>You may export your club data in standard formats</li>
                                    <li>Data exports include game statistics, player information, and financial records</li>
                                    <li>Export functionality may be limited based on your subscription plan</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">7. Intellectual Property Rights</h2>

                                <h4 className="h5 mb-2">7.1 Our Rights</h4>
                                <ul className="mb-3">
                                    <li>We own all rights to the Platform software, design, and functionality</li>
                                    <li>Our trademarks, logos, and brand elements are protected intellectual property</li>
                                    <li>You may not copy, modify, or redistribute our software without permission</li>
                                </ul>

                                <h4 className="h5 mb-2">7.2 Your Rights</h4>
                                <ul className="mb-3">
                                    <li>You retain ownership of your club data and member information</li>
                                    <li>You grant us a license to process and store your data for service provision</li>
                                    <li>You may use our Platform according to these Terms during your subscription</li>
                                </ul>

                                <h4 className="h5 mb-2">7.3 Third-Party Content</h4>
                                <ul className="mb-3">
                                    <li>Some features may integrate with third-party services and content</li>
                                    <li>Third-party content is subject to their respective terms and conditions</li>
                                    <li>We are not responsible for third-party service availability or performance</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">8. Service Availability and Support</h2>

                                <h4 className="h5 mb-2">8.1 Uptime and Availability</h4>
                                <ul className="mb-3">
                                    <li>We strive for 99.9% uptime but cannot guarantee continuous availability</li>
                                    <li>Scheduled maintenance will be announced with reasonable advance notice</li>
                                    <li>Emergency maintenance may be performed without prior notice</li>
                                </ul>

                                <h4 className="h5 mb-2">8.2 Customer Support</h4>
                                <ul className="mb-3">
                                    <li>Technical support is provided during business hours via email and chat</li>
                                    <li>Response times vary based on subscription plan and issue severity</li>
                                    <li>Training resources and documentation are available online</li>
                                    <li>Premium support options may be available for Enterprise plans</li>
                                </ul>

                                <h4 className="h5 mb-2">8.3 Updates and Modifications</h4>
                                <ul className="mb-3">
                                    <li>We regularly update the Platform with new features and improvements</li>
                                    <li>Minor updates are deployed automatically with minimal disruption</li>
                                    <li>Major changes will be communicated in advance when possible</li>
                                    <li>We reserve the right to modify or discontinue features with notice</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">9. Liability and Disclaimers</h2>

                                <h4 className="h5 mb-2">9.1 Service Disclaimers</h4>
                                <p>The Platform is provided "as is" without warranties of any kind. We disclaim:</p>
                                <ul className="mb-3">
                                    <li>Warranties of merchantability, fitness for a particular purpose</li>
                                    <li>Guarantees of error-free operation or data accuracy</li>
                                    <li>Responsibility for third-party integrations and services</li>
                                    <li>Liability for business losses due to service interruptions</li>
                                </ul>

                                <h4 className="h5 mb-2">9.2 Limitation of Liability</h4>
                                <ul className="mb-3">
                                    <li>Our liability is limited to the amount of subscription fees paid in the past 12 months</li>
                                    <li>We are not liable for indirect, incidental, or consequential damages</li>
                                    <li>This includes loss of profits, data, or business opportunities</li>
                                    <li>Some jurisdictions may not allow these limitations</li>
                                </ul>

                                <h4 className="h5 mb-2">9.3 User Responsibilities</h4>
                                <ul className="mb-3">
                                    <li>You are responsible for compliance with local gaming and business laws</li>
                                    <li>Regular data backups and security measures are your responsibility</li>
                                    <li>You must ensure appropriate use by all club members and staff</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">10. Termination</h2>

                                <h4 className="h5 mb-2">10.1 Termination by You</h4>
                                <ul className="mb-3">
                                    <li>You may cancel your subscription at any time through your account settings</li>
                                    <li>Cancellation takes effect at the end of your current billing period</li>
                                    <li>No refunds are provided for unused subscription time</li>
                                    <li>Data export tools remain available for 30 days after cancellation</li>
                                </ul>

                                <h4 className="h5 mb-2">10.2 Termination by Us</h4>
                                <p>We may terminate your account immediately for:</p>
                                <ul className="mb-3">
                                    <li>Violation of these Terms or our Acceptable Use Policy</li>
                                    <li>Non-payment of subscription fees after the grace period</li>
                                    <li>Fraudulent activity or misuse of the Platform</li>
                                    <li>Legal requirements or court orders</li>
                                </ul>

                                <h4 className="h5 mb-2">10.3 Effects of Termination</h4>
                                <ul className="mb-3">
                                    <li>All access to the Platform will be immediately suspended</li>
                                    <li>Your data will be retained for 90 days for potential recovery</li>
                                    <li>After 90 days, all data may be permanently deleted</li>
                                    <li>Outstanding payment obligations remain due</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">11. Dispute Resolution</h2>

                                <h4 className="h5 mb-2">11.1 Informal Resolution</h4>
                                <ul className="mb-3">
                                    <li>We encourage resolving disputes through direct communication first</li>
                                    <li>Contact our support team with any concerns or complaints</li>
                                    <li>We will make reasonable efforts to address issues promptly</li>
                                </ul>

                                <h4 className="h5 mb-2">11.2 Formal Dispute Process</h4>
                                <ul className="mb-3">
                                    <li>Unresolved disputes may be submitted to binding arbitration</li>
                                    <li>Arbitration will be conducted under relevant commercial arbitration rules</li>
                                    <li>The arbitration location will be [Your Jurisdiction]</li>
                                    <li>Each party bears their own legal costs unless otherwise awarded</li>
                                </ul>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">12. General Provisions</h2>

                                <h4 className="h5 mb-2">12.1 Governing Law</h4>
                                <p>
                                    These Terms are governed by the laws of [Your Jurisdiction] without regard to
                                    conflict of law principles.
                                </p>

                                <h4 className="h5 mb-2">12.2 Amendments</h4>
                                <p>
                                    We may modify these Terms at any time by posting updated terms on our Platform.
                                    Continued use constitutes acceptance of the modified Terms.
                                </p>

                                <h4 className="h5 mb-2">12.3 Severability</h4>
                                <p>
                                    If any provision of these Terms is found to be unenforceable, the remaining
                                    provisions will continue in full force and effect.
                                </p>

                                <h4 className="h5 mb-2">12.4 Assignment</h4>
                                <p>
                                    You may not assign your rights or obligations under these Terms without our consent.
                                    We may assign our rights and obligations to any third party.
                                </p>
                            </section>

                            <section className="mb-4">
                                <h2 className="h3 mb-3">13. Contact Information</h2>
                                <div className="alert alert-info legal-contact-box">
                                    <p className="mb-2"><strong>Questions About These Terms?</strong></p>
                                    <p className="mb-0">Email: <a href="mailto:contact@cueclubmanager.com">contact@cueclubmanager.com</a></p>
                                </div>
                            </section>
                    </div>
                </div>
            </div>
        </PublicLegalLayout>
    );
}

export default GeneralConditions;
